import {
    Checkbox,
    Chip,
    CloseButton,
    Dialog,
    Grid,
    newToast,
    Stack,
    Typography,
    ListContext,
    ListContextValue,
    CancelIconButton,
} from "@compass/core";
import { Select, SubmitButton } from "components"; 
import { ClassBulkEditResource, ClassBulkUpdateDto, LkClassLevel, LkClassType } from "generated/openApi";
import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";

interface ClassBulkEditPageProps {
    open: boolean;
    onClose: () => void;
    selectedIds: number[];
}


interface IFormData {
    updateClassType: boolean;
    classTypeId: number | undefined;
    updateClassLevel: boolean;
    classLevelId: number | undefined;
    //updateIsLocked: boolean;
    //isLocked: boolean;
}

export const ClassBulkEditPage = (props: ClassBulkEditPageProps) => {
    const { open, onClose, selectedIds } = props;
    const listContext = useContext<ListContextValue>(ListContext);
    const dispatch = useDispatch();

    const [resource, setResource] = useState<ClassBulkEditResource>({});
    const [currentSelectedIds, setCurrentSelectedIds] = useState<number[]>(selectedIds);

    const [formData, setFormData] = useState<IFormData>({
        updateClassType: false,
        classTypeId: undefined,
        updateClassLevel: false,
        classLevelId: undefined,
        //updateIsLocked: false,
        //isLocked: false,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setCurrentSelectedIds(selectedIds);
            ApiService.classes.getBulkEditResource(selectedIds).then(setResource);
            setFormData({
                updateClassType: false,
                classTypeId: undefined,
                updateClassLevel: false,
                classLevelId: undefined,
            });
        }
    }, [open, selectedIds]);

    useEffect(() => {
        if (open && currentSelectedIds.length > 0) {
            ApiService.classes.getBulkEditResource(currentSelectedIds).then(setResource);
        }
    }, [currentSelectedIds, open]);

    const handleRemoveClass = (classId: number) => {
        const updatedIds = currentSelectedIds.filter(id => id !== classId);
        setCurrentSelectedIds(updatedIds);
    };

    const handleSave = () => {
        setIsSubmitting(true);

        const payload: ClassBulkUpdateDto = {
            ids: currentSelectedIds,
        };
        if (formData.updateClassType) payload.classTypeId = formData.classTypeId;
        if (formData.updateClassLevel) payload.classLevelId = formData.classLevelId;
        //if (formData.updateIsLocked) payload.isLocked = formData.isLocked;

        ApiService.classes
            .saveBulkUpdate(payload)
            .then(() => {
                dispatch(setAppToast(newToast("success", "Classes updated successfully.")));
                listContext.loadList?.();
                listContext.clearSelectedIds?.();
                onClose();
            })
            .catch(() => {
                dispatch(setAppToast(newToast("error", "An error occurred while updating classes.")));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const dialogHeader = (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit Classes</Typography>
            <CloseButton theme="Dark" onClick={onClose} />
        </Stack>
    );

    const dialogBody = (
        <Stack spacing="lg">
            <Grid container spacing="sm" alignItems="center">
                <Grid item xs={3}>
                    <Checkbox
                        label="Class Type"
                        checked={formData.updateClassType}
                        onChange={(e) =>
                            setFormData(produce(formData, (draft) => {
                                draft.updateClassType = e.target.checked;
                            }))
                        }
                    />
                </Grid>
                <Grid item xs={9}>
                    <Select
                        disabled={!formData.updateClassType}
                        options={resource.classTypes || []}
                        getOptionLabel={(x: LkClassType) => x.name}
                        value={resource.classTypes?.find((x) => x.id === formData.classTypeId)}
                        onChange={(e, value) =>
                            setFormData(produce(formData, (draft) => {
                                draft.classTypeId = value?.id;
                            }))
                        }
                    />
                </Grid>

                <Grid item xs={3}>
                    <Checkbox
                        label="Class Level"
                        checked={formData.updateClassLevel}
                        onChange={(e) =>
                            setFormData(produce(formData, (draft) => {
                                draft.updateClassLevel = e.target.checked;
                            }))
                        }
                    />
                </Grid>
                <Grid item xs={9}>
                    <Select
                        disabled={!formData.updateClassLevel}
                        options={resource.classLevels || []}
                        getOptionLabel={(x: LkClassLevel) => x.name}
                        value={resource.classLevels?.find((x) => x.id === formData.classLevelId)}
                        onChange={(e, value) =>
                            setFormData(produce(formData, (draft) => {
                                draft.classLevelId = value?.id;
                            }))
                        }
                    />
                </Grid>
            </Grid>
            <Stack spacing="xs">
                <Typography variant="overline">Selected ({currentSelectedIds.length})</Typography>
                <Grid container spacing="xs">
                    {resource.selectedClasses?.map((c) => (
                        <Grid item xs={4} key={c.id}>
                            <Stack
                                direction="row"
                                spacing="md"
                                border={1}
                                borderColor={(theme) => theme.palette.border.dark}
                                alignItems="center"
                                justifyContent="space-between"
                                p={0.5}
                                sx={{
                                    "&:hover": { bgcolor: (theme) => theme.palette.background.default },
                                }}
                            >
                                <Stack spacing="xs" alignItems="flex-start">
                                    <Typography variant="body2">{c.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ID: {c.id}
                                    </Typography>
                                </Stack>
                                <CancelIconButton onClick={() => handleRemoveClass(c.id)} />
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Stack>
        </Stack>
    );

    const dialogFooter = (
        <Stack direction="row" justifyContent="flex-end">
            <SubmitButton
                onClick={handleSave}
                disabled={isSubmitting || currentSelectedIds.length === 0}
                label="Update"
            />
        </Stack>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            header={dialogHeader}
            content={dialogBody}
            footer={dialogFooter}
            maxWidth="md"
        />
    );
};